import { ReactNode } from 'react';
import { Redirect, useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import { AuthSurface } from '@/components/v2/auth/AuthSurface';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isValidating, sessionExpired } = useAuth();
  const [, setLocation] = useLocation();

  // Wait for an in-flight session validation/refresh before mounting protected
  // content. Even when optimistically authenticated from cached credentials,
  // the access cookie may be stale and must be refreshed first — otherwise the
  // page's authenticated data fetches race ahead and 401 (home-page
  // error-on-first-load bug). `isValidating` is cache-aware (see useAuth): a
  // recently validated session reports false immediately, so this adds no
  // loader flash for the common case; only a genuinely stale session waits.
  if (isValidating || (isLoading && !isAuthenticated)) {
    return <AuthSurface variant="refreshing" />;
  }

  if (!isAuthenticated) {
    if (sessionExpired) {
      const redirectTo = encodeURIComponent(window.location.pathname + window.location.search);
      return (
        <AuthSurface
          variant="expired"
          onSignIn={() => setLocation('/login?redirect_to=' + redirectTo)}
        />
      );
    }
    return <Redirect to="/login" />;
  }

  return <>{children}</>;
}
